import React, { useState } from "react";

import {
  BriefcaseBusiness,
  FileText,
  MapPin,
  CalendarDays,
  IndianRupee,
  Users,
  Clock3,
  Code2,
  ChevronRight,
  ChevronLeft,
  CheckCircle2,
  X,
  Plus,
  Sparkles,
} from "lucide-react";

function FreelanceProjectPost() {
  // =====================================================
  // API
  // =====================================================

  const API_URL =
    "/api/freelance/projects";

  // =====================================================
  // STATE
  // =====================================================

  const [step, setStep] = useState(1);

  const [formData, setFormData] = useState({
    projectTitle: "",
    category: "",
    projectType: "FIXED",
    projectDescription: "",

    skills: [],
    skillInput: "",

    locationType: "REMOTE",
    location: "",

    experienceRequired: "",

    budgetType: "FIXED",
    budgetMin: "",
    budgetMax: "",

    paymentType: "PROJECT",

    duration: "",

    startDate: "",
    workingHours: "",

    vacancies: 1,

    deadline: "",

    requirements: "",
    deliverables: "",

    preferredCommunication: "CHAT",
    additionalNotes: "",
  });

  const [errors, setErrors] = useState({});

  const [posted, setPosted] = useState(false);

  const [loading, setLoading] = useState(false);

  const [apiError, setApiError] = useState("");

  const [createdProject, setCreatedProject] = useState(null);

  // =====================================================
  // HANDLE INPUT
  // =====================================================

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    setErrors((prev) => ({
      ...prev,
      [name]: "",
    }));

    setApiError("");
  };

  // =====================================================
  // ADD SKILL
  // =====================================================

  const addSkill = () => {
    const skill = formData.skillInput.trim();

    if (!skill) return;

    const alreadyExists = formData.skills.some(
      (item) =>
        item.toLowerCase() === skill.toLowerCase()
    );

    if (alreadyExists) {
      setFormData((prev) => ({
        ...prev,
        skillInput: "",
      }));

      return;
    }

    setFormData((prev) => ({
      ...prev,

      skills: [
        ...prev.skills,
        skill,
      ],

      skillInput: "",
    }));

    setErrors((prev) => ({
      ...prev,
      skills: "",
    }));
  };

  // =====================================================
  // REMOVE SKILL
  // =====================================================

  const removeSkill = (skill) => {
    setFormData((prev) => ({
      ...prev,

      skills: prev.skills.filter(
        (item) => item !== skill
      ),
    }));
  };

  // =====================================================
  // SKILL ENTER
  // =====================================================

  const handleSkillKeyDown = (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addSkill();
    }
  };

  // =====================================================
  // VALIDATION
  // =====================================================

  const validateStep = () => {
    const newErrors = {};

    // ---------------------------------------------------
    // STEP 1
    // ---------------------------------------------------

    if (step === 1) {
      if (!formData.projectTitle.trim()) {
        newErrors.projectTitle =
          "Project title is required.";
      }

      if (!formData.category) {
        newErrors.category =
          "Please select a category.";
      }

      if (!formData.projectDescription.trim()) {
        newErrors.projectDescription =
          "Project description is required.";
      }
    }

    // ---------------------------------------------------
    // STEP 2
    // ---------------------------------------------------

    if (step === 2) {
      if (formData.skills.length === 0) {
        newErrors.skills =
          "Add at least one skill.";
      }

      if (!formData.experienceRequired) {
        newErrors.experienceRequired =
          "Select required experience.";
      }

      if (
        !formData.vacancies ||
        Number(formData.vacancies) < 1
      ) {
        newErrors.vacancies =
          "At least one freelancer is required.";
      }
    }

    // ---------------------------------------------------
    // STEP 3
    // ---------------------------------------------------

    if (step === 3) {
      if (
        formData.budgetMin === "" ||
        formData.budgetMin === null
      ) {
        newErrors.budgetMin =
          "Enter minimum budget.";
      }

      if (
        formData.budgetMax === "" ||
        formData.budgetMax === null
      ) {
        newErrors.budgetMax =
          "Enter maximum budget.";
      }

      if (
        formData.budgetMin !== "" &&
        formData.budgetMax !== "" &&
        Number(formData.budgetMin) >
          Number(formData.budgetMax)
      ) {
        newErrors.budgetMax =
          "Maximum budget must be greater than minimum budget.";
      }

      if (!formData.duration) {
        newErrors.duration =
          "Select project duration.";
      }
    }

    // ---------------------------------------------------
    // LOCATION
    // ---------------------------------------------------

    if (
      step === 3 &&
      formData.locationType !== "REMOTE" &&
      !formData.location.trim()
    ) {
      newErrors.location =
        "Location is required for on-site or hybrid projects.";
    }

    setErrors(newErrors);

    return (
      Object.keys(newErrors).length === 0
    );
  };

  // =====================================================
  // NEXT
  // =====================================================

  const nextStep = () => {
    if (!validateStep()) {
      return;
    }

    if (step < 4) {
      setStep(step + 1);

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  // =====================================================
  // PREVIOUS
  // =====================================================

  const previousStep = () => {
    if (step > 1) {
      setStep(step - 1);

      window.scrollTo({
        top: 0,
        behavior: "smooth",
      });
    }
  };

  // =====================================================
  // RESET FORM
  // =====================================================

  const resetForm = () => {
    setFormData({
      projectTitle: "",
      category: "",
      projectType: "FIXED",
      projectDescription: "",

      skills: [],
      skillInput: "",

      locationType: "REMOTE",
      location: "",

      experienceRequired: "",

      budgetType: "FIXED",
      budgetMin: "",
      budgetMax: "",

      paymentType: "PROJECT",

      duration: "",

      startDate: "",
      workingHours: "",

      vacancies: 1,

      deadline: "",

      requirements: "",
      deliverables: "",

      preferredCommunication: "CHAT",
      additionalNotes: "",
    });

    setErrors({});
    setApiError("");
    setCreatedProject(null);
    setPosted(false);
    setStep(1);
  };

  // =====================================================
  // POST PROJECT API
  // =====================================================

  const handlePostProject = async () => {
    if (!validateStep()) {
      return;
    }

    setLoading(true);
    setApiError("");

    try {
      // ================================================
      // BACKEND DTO MATCHING PAYLOAD
      // ================================================

      const projectPayload = {
        projectTitle:
          formData.projectTitle.trim(),

        category:
          formData.category,

        projectType:
          formData.projectType,

        projectDescription:
          formData.projectDescription.trim(),

        // IMPORTANT:
        // DTO expects List<String>
        skills:
          formData.skills,

        locationType:
          formData.locationType,

        location:
          formData.locationType === "REMOTE"
            ? "Remote"
            : formData.location.trim(),

        experienceRequired:
          formData.experienceRequired,

        budgetType:
          formData.budgetType,

        budgetMin:
          Number(formData.budgetMin),

        budgetMax:
          Number(formData.budgetMax),

        paymentType:
          formData.paymentType,

        duration:
          formData.duration,

        startDate:
          formData.startDate || null,

        workingHours:
          formData.workingHours.trim(),

        vacancies:
          Number(formData.vacancies),

        deadline:
          formData.deadline || null,

        requirements:
          formData.requirements.trim(),

        deliverables:
          formData.deliverables.trim(),

        preferredCommunication:
          formData.preferredCommunication,

        additionalNotes:
          formData.additionalNotes.trim(),
      };

      console.log(
        "===================================="
      );

      console.log(
        "POST FREELANCE PROJECT"
      );

      console.log(
        "API:",
        API_URL
      );

      console.log(
        "PAYLOAD:",
        projectPayload
      );

      console.log(
        "===================================="
      );

      // ================================================
      // API CALL
      // ================================================

      const response = await fetch(
        API_URL,
        {
          method: "POST",

          headers: {
            "Content-Type":
              "application/json",

            Accept:
              "application/json",
          },

          body: JSON.stringify(
            projectPayload
          ),
        }
      );

      // ================================================
      // READ RESPONSE
      // ================================================

      let responseData = null;

      const contentType =
        response.headers.get(
          "content-type"
        );

      if (
        contentType &&
        contentType.includes(
          "application/json"
        )
      ) {
        responseData =
          await response.json();
      } else {
        const text =
          await response.text();

        responseData = text;
      }

      console.log(
        "API STATUS:",
        response.status
      );

      console.log(
        "API RESPONSE:",
        responseData
      );

      // ================================================
      // API ERROR
      // ================================================

      if (!response.ok) {
        let message =
          "Unable to create freelance project.";

        if (
          responseData &&
          typeof responseData === "object"
        ) {
          message =
            responseData.message ||
            responseData.error ||
            message;
        }

        if (
          typeof responseData === "string" &&
          responseData.trim()
        ) {
          message =
            responseData;
        }

        throw new Error(
          `${message} (HTTP ${response.status})`
        );
      }

      // ================================================
      // SUCCESS
      // ================================================

      setCreatedProject(
        responseData
      );

      setPosted(true);

    } catch (error) {
      console.error(
        "Freelance Project API Error:",
        error
      );

      // ================================================
      // CORS / NETWORK ERROR
      // ================================================

      if (
        error instanceof TypeError
      ) {
        setApiError(
          "Unable to connect with server. Please make sure Spring Boot backend is running on  and CORS is enabled."
        );
      } else {
        setApiError(
          error.message ||
            "Something went wrong while posting the project."
        );
      }

    } finally {
      setLoading(false);
    }
  };

  // =====================================================
  // SUCCESS SCREEN
  // =====================================================

  if (posted) {
    return (
      <div className="fp-page">

        <div className="fp-success">

          <div className="fp-success-icon">
            <CheckCircle2 size={46} />
          </div>

          <h1>
            Project Posted Successfully
          </h1>

          <p>
            Your freelance project has
            been created successfully.
            Freelancers can now view
            and apply to your project.
          </p>

          <div className="fp-success-details">

            <div>
              <span>
                Project
              </span>

              <strong>
                {formData.projectTitle}
              </strong>
            </div>

            <div>
              <span>
                Budget
              </span>

              <strong>
                ₹
                {Number(
                  formData.budgetMin || 0
                ).toLocaleString("en-IN")}

                {" - "}

                ₹
                {Number(
                  formData.budgetMax || 0
                ).toLocaleString("en-IN")}
              </strong>
            </div>

            <div>
              <span>
                Duration
              </span>

              <strong>
                {formData.duration}
              </strong>
            </div>

          </div>

          {createdProject &&
            createdProject.id && (
              <div className="fp-created-id">
                Project ID:
                <strong>
                  #{createdProject.id}
                </strong>
              </div>
            )}

          <button
            className="fp-primary-btn"
            onClick={resetForm}
          >
            Post Another Project
          </button>

        </div>

      </div>
    );
  }

  // =====================================================
  // MAIN UI
  // =====================================================

  return (
    <div className="fp-page">

      {/* =================================================
          HEADER
      ================================================= */}

      <div className="fp-header">

        <div className="fp-header-left">

          <div className="fp-header-icon">
            <Sparkles size={23} />
          </div>

          <div>

            <h1>
              Post a Freelance Project
            </h1>

            <p>
              Find the right freelancer
              for your project
            </p>

          </div>

        </div>

        <div className="fp-header-badge">

          <BriefcaseBusiness size={14} />

          Freelance Marketplace

        </div>

      </div>

      {/* =================================================
          API ERROR
      ================================================= */}

      {apiError && (
        <div className="fp-api-error">

          <strong>
            Project Posting Failed
          </strong>

          <span>
            {apiError}
          </span>

          <button
            type="button"
            onClick={() =>
              setApiError("")
            }
          >
            <X size={15} />
          </button>

        </div>
      )}

      {/* =================================================
          STEPPER
      ================================================= */}

      <div className="fp-stepper">

        {[
          {
            id: 1,
            title: "Project Details",
          },
          {
            id: 2,
            title: "Skills & Expertise",
          },
          {
            id: 3,
            title: "Budget & Timeline",
          },
          {
            id: 4,
            title: "Requirements",
          },
        ].map((item, index) => (

          <React.Fragment
            key={item.id}
          >

            <div
              className={
                `fp-step ${
                  step === item.id
                    ? "active"
                    : step > item.id
                    ? "completed"
                    : ""
                }`
              }
            >

              <div className="fp-step-number">

                {step > item.id ? (
                  <CheckCircle2 size={17} />
                ) : (
                  item.id
                )}

              </div>

              <span>
                {item.title}
              </span>

            </div>

            {index < 3 && (

              <div
                className={
                  `fp-step-line ${
                    step > item.id
                      ? "completed"
                      : ""
                  }`
                }
              />

            )}

          </React.Fragment>

        ))}

      </div>

      {/* =================================================
          MAIN
      ================================================= */}

      <div className="fp-container">

        {/* =================================================
            STEP 1
        ================================================= */}

        {step === 1 && (

          <div className="fp-card">

            <div className="fp-card-header">

              <div className="fp-section-icon">

                <FileText size={19} />

              </div>

              <div>

                <h2>
                  Tell us about your project
                </h2>

                <p>
                  Provide the basic information
                  freelancers need to understand
                  your project.
                </p>

              </div>

            </div>

            <div className="fp-form-grid">

              {/* PROJECT TITLE */}

              <div className="fp-field full">

                <label>
                  Project Title
                  <span>*</span>
                </label>

                <input
                  name="projectTitle"
                  value={
                    formData.projectTitle
                  }
                  onChange={
                    handleChange
                  }
                  placeholder="e.g. Build a React Admin Dashboard"
                />

                {errors.projectTitle && (
                  <small className="fp-error">
                    {errors.projectTitle}
                  </small>
                )}

              </div>

              {/* CATEGORY */}

              <div className="fp-field">

                <label>
                  Project Category
                  <span>*</span>
                </label>

                <select
                  name="category"
                  value={
                    formData.category
                  }
                  onChange={
                    handleChange
                  }
                >

                  <option value="">
                    Select category
                  </option>

                  <option value="WEB_DEVELOPMENT">
                    Web Development
                  </option>

                  <option value="APP_DEVELOPMENT">
                    App Development
                  </option>

                  <option value="SOFTWARE_DEVELOPMENT">
                    Software Development
                  </option>

                  <option value="AI_ML">
                    AI / Machine Learning
                  </option>

                  <option value="DATA_SCIENCE">
                    Data Science
                  </option>

                  <option value="UI_UX">
                    UI / UX Design
                  </option>

                  <option value="DIGITAL_MARKETING">
                    Digital Marketing
                  </option>

                  <option value="CONTENT_WRITING">
                    Content Writing
                  </option>

                  <option value="VIDEO_EDITING">
                    Video Editing
                  </option>

                  <option value="OTHER">
                    Other
                  </option>

                </select>

                {errors.category && (
                  <small className="fp-error">
                    {errors.category}
                  </small>
                )}

              </div>

              {/* PROJECT TYPE */}

              <div className="fp-field">

                <label>
                  Project Type
                </label>

                <div className="fp-radio-group">

                  <label className="fp-radio-card">

                    <input
                      type="radio"
                      name="projectType"
                      value="FIXED"
                      checked={
                        formData.projectType ===
                        "FIXED"
                      }
                      onChange={
                        handleChange
                      }
                    />

                    <div>

                      <strong>
                        Fixed Project
                      </strong>

                      <span>
                        Defined scope & budget
                      </span>

                    </div>

                  </label>

                  <label className="fp-radio-card">

                    <input
                      type="radio"
                      name="projectType"
                      value="HOURLY"
                      checked={
                        formData.projectType ===
                        "HOURLY"
                      }
                      onChange={
                        handleChange
                      }
                    />

                    <div>

                      <strong>
                        Hourly
                      </strong>

                      <span>
                        Pay based on hours
                      </span>

                    </div>

                  </label>

                </div>

              </div>

              {/* DESCRIPTION */}

              <div className="fp-field full">

                <label>
                  Project Description
                  <span>*</span>
                </label>

                <textarea
                  name="projectDescription"
                  value={
                    formData.projectDescription
                  }
                  onChange={
                    handleChange
                  }
                  rows="7"
                  placeholder="Describe what you want to build, your goals, expected functionality, and important project details..."
                />

                <div className="fp-character-count">

                  {
                    formData.projectDescription
                      .length
                  }{" "}
                  characters

                </div>

                {errors.projectDescription && (
                  <small className="fp-error">
                    {
                      errors.projectDescription
                    }
                  </small>
                )}

              </div>

            </div>

          </div>

        )}

        {/* =================================================
            STEP 2
        ================================================= */}

        {step === 2 && (

          <div className="fp-card">

            <div className="fp-card-header">

              <div className="fp-section-icon">

                <Code2 size={19} />

              </div>

              <div>

                <h2>
                  Skills & Freelancer Expertise
                </h2>

                <p>
                  Define the skills and experience
                  required for this project.
                </p>

              </div>

            </div>

            <div className="fp-form-grid">

              {/* SKILLS */}

              <div className="fp-field full">

                <label>
                  Required Skills
                  <span>*</span>
                </label>

                <div className="fp-skill-input">

                  <input
                    value={
                      formData.skillInput
                    }
                    onChange={(e) =>
                      setFormData(
                        (prev) => ({
                          ...prev,
                          skillInput:
                            e.target.value,
                        })
                      )
                    }
                    onKeyDown={
                      handleSkillKeyDown
                    }
                    placeholder="Type a skill and press Enter"
                  />

                  <button
                    type="button"
                    onClick={
                      addSkill
                    }
                  >

                    <Plus size={16} />

                    Add

                  </button>

                </div>

                {formData.skills.length >
                  0 && (

                  <div className="fp-skills">

                    {formData.skills.map(
                      (skill) => (

                        <span
                          key={skill}
                          className="fp-skill-tag"
                        >

                          {skill}

                          <button
                            type="button"
                            onClick={() =>
                              removeSkill(
                                skill
                              )
                            }
                          >

                            <X size={12} />

                          </button>

                        </span>

                      )
                    )}

                  </div>

                )}

                {errors.skills && (
                  <small className="fp-error">
                    {errors.skills}
                  </small>
                )}

              </div>

              {/* EXPERIENCE */}

              <div className="fp-field">

                <label>
                  Experience Required
                  <span>*</span>
                </label>

                <select
                  name="experienceRequired"
                  value={
                    formData.experienceRequired
                  }
                  onChange={
                    handleChange
                  }
                >

                  <option value="">
                    Select experience
                  </option>

                  <option value="FRESHER">
                    Fresher
                  </option>

                  <option value="1-2 YEARS">
                    1–2 Years
                  </option>

                  <option value="2-4 YEARS">
                    2–4 Years
                  </option>

                  <option value="4-6 YEARS">
                    4–6 Years
                  </option>

                  <option value="6+ YEARS">
                    6+ Years
                  </option>

                </select>

                {errors.experienceRequired && (
                  <small className="fp-error">
                    {
                      errors.experienceRequired
                    }
                  </small>
                )}

              </div>

              {/* VACANCIES */}

              <div className="fp-field">

                <label>
                  Number of Freelancers
                </label>

                <div className="fp-input-icon">

                  <Users size={16} />

                  <input
                    type="number"
                    min="1"
                    name="vacancies"
                    value={
                      formData.vacancies
                    }
                    onChange={
                      handleChange
                    }
                  />

                </div>

                {errors.vacancies && (
                  <small className="fp-error">
                    {errors.vacancies}
                  </small>
                )}

              </div>

              {/* COMMUNICATION */}

              <div className="fp-field">

                <label>
                  Preferred Communication
                </label>

                <select
                  name="preferredCommunication"
                  value={
                    formData.preferredCommunication
                  }
                  onChange={
                    handleChange
                  }
                >

                  <option value="CHAT">
                    Platform Chat
                  </option>

                  <option value="EMAIL">
                    Email
                  </option>

                  <option value="VIDEO_CALL">
                    Video Call
                  </option>

                  <option value="PHONE">
                    Phone
                  </option>

                </select>

              </div>

              {/* WORKING HOURS */}

              <div className="fp-field">

                <label>
                  Working Hours
                </label>

                <input
                  name="workingHours"
                  value={
                    formData.workingHours
                  }
                  onChange={
                    handleChange
                  }
                  placeholder="e.g. 4–6 hours/day"
                />

              </div>

            </div>

          </div>

        )}

        {/* =================================================
            STEP 3
        ================================================= */}

        {step === 3 && (

          <div className="fp-card">

            <div className="fp-card-header">

              <div className="fp-section-icon">

                <IndianRupee size={19} />

              </div>

              <div>

                <h2>
                  Budget & Timeline
                </h2>

                <p>
                  Set your project budget and
                  expected delivery timeline.
                </p>

              </div>

            </div>

            <div className="fp-form-grid">

              {/* BUDGET TYPE */}

              <div className="fp-field">

                <label>
                  Budget Type
                </label>

                <select
                  name="budgetType"
                  value={
                    formData.budgetType
                  }
                  onChange={
                    handleChange
                  }
                >

                  <option value="FIXED">
                    Fixed Budget
                  </option>

                  <option value="HOURLY">
                    Hourly Rate
                  </option>

                  <option value="NEGOTIABLE">
                    Negotiable
                  </option>

                </select>

              </div>

              {/* PAYMENT TYPE */}

              <div className="fp-field">

                <label>
                  Payment Type
                </label>

                <select
                  name="paymentType"
                  value={
                    formData.paymentType
                  }
                  onChange={
                    handleChange
                  }
                >

                  <option value="PROJECT">
                    Full Project
                  </option>

                  <option value="MILESTONE">
                    Milestone Based
                  </option>

                  <option value="HOURLY">
                    Hourly
                  </option>

                </select>

              </div>

              {/* MIN BUDGET */}

              <div className="fp-field">

                <label>
                  Minimum Budget
                  <span>*</span>
                </label>

                <div className="fp-input-icon">

                  <IndianRupee size={16} />

                  <input
                    type="number"
                    min="0"
                    name="budgetMin"
                    value={
                      formData.budgetMin
                    }
                    onChange={
                      handleChange
                    }
                    placeholder="50,000"
                  />

                </div>

                {errors.budgetMin && (
                  <small className="fp-error">
                    {errors.budgetMin}
                  </small>
                )}

              </div>

              {/* MAX BUDGET */}

              <div className="fp-field">

                <label>
                  Maximum Budget
                  <span>*</span>
                </label>

                <div className="fp-input-icon">

                  <IndianRupee size={16} />

                  <input
                    type="number"
                    min="0"
                    name="budgetMax"
                    value={
                      formData.budgetMax
                    }
                    onChange={
                      handleChange
                    }
                    placeholder="1,00,000"
                  />

                </div>

                {errors.budgetMax && (
                  <small className="fp-error">
                    {errors.budgetMax}
                  </small>
                )}

              </div>

              {/* DURATION */}

              <div className="fp-field">

                <label>
                  Project Duration
                  <span>*</span>
                </label>

                <select
                  name="duration"
                  value={
                    formData.duration
                  }
                  onChange={
                    handleChange
                  }
                >

                  <option value="">
                    Select duration
                  </option>

                  <option value="LESS_THAN_1_WEEK">
                    Less than 1 week
                  </option>

                  <option value="1-2 WEEKS">
                    1–2 weeks
                  </option>

                  <option value="2-4 WEEKS">
                    2–4 weeks
                  </option>

                  <option value="1-2 MONTHS">
                    1–2 months
                  </option>

                  <option value="2-3 MONTHS">
                    2–3 months
                  </option>

                  <option value="3+ MONTHS">
                    3+ months
                  </option>

                </select>

                {errors.duration && (
                  <small className="fp-error">
                    {errors.duration}
                  </small>
                )}

              </div>

              {/* START DATE */}

              <div className="fp-field">

                <label>
                  Expected Start Date
                </label>

                <div className="fp-input-icon">

                  <CalendarDays size={16} />

                  <input
                    type="date"
                    name="startDate"
                    value={
                      formData.startDate
                    }
                    onChange={
                      handleChange
                    }
                  />

                </div>

              </div>

              {/* DEADLINE */}

              <div className="fp-field">

                <label>
                  Application Deadline
                </label>

                <div className="fp-input-icon">

                  <Clock3 size={16} />

                  <input
                    type="date"
                    name="deadline"
                    value={
                      formData.deadline
                    }
                    onChange={
                      handleChange
                    }
                  />

                </div>

              </div>

              {/* LOCATION TYPE */}

              <div className="fp-field">

                <label>
                  Work Location
                </label>

                <select
                  name="locationType"
                  value={
                    formData.locationType
                  }
                  onChange={
                    handleChange
                  }
                >

                  <option value="REMOTE">
                    Remote
                  </option>

                  <option value="ONSITE">
                    On-site
                  </option>

                  <option value="HYBRID">
                    Hybrid
                  </option>

                </select>

              </div>

              {/* LOCATION */}

              {formData.locationType !==
                "REMOTE" && (

                <div className="fp-field">

                  <label>
                    Location
                    <span>*</span>
                  </label>

                  <div className="fp-input-icon">

                    <MapPin size={16} />

                    <input
                      name="location"
                      value={
                        formData.location
                      }
                      onChange={
                        handleChange
                      }
                      placeholder="e.g. Noida, Uttar Pradesh"
                    />

                  </div>

                  {errors.location && (
                    <small className="fp-error">
                      {errors.location}
                    </small>
                  )}

                </div>

              )}

            </div>

          </div>

        )}

        {/* =================================================
            STEP 4
        ================================================= */}

        {step === 4 && (

          <div className="fp-card">

            <div className="fp-card-header">

              <div className="fp-section-icon">

                <CheckCircle2 size={19} />

              </div>

              <div>

                <h2>
                  Requirements & Deliverables
                </h2>

                <p>
                  Add clear expectations so
                  freelancers can submit relevant
                  proposals.
                </p>

              </div>

            </div>

            <div className="fp-form-grid">

              {/* REQUIREMENTS */}

              <div className="fp-field full">

                <label>
                  Project Requirements
                </label>

                <textarea
                  name="requirements"
                  value={
                    formData.requirements
                  }
                  onChange={
                    handleChange
                  }
                  rows="6"
                  placeholder="Mention technical requirements, preferred tools, integrations, platform requirements, or other important expectations..."
                />

              </div>

              {/* DELIVERABLES */}

              <div className="fp-field full">

                <label>
                  Expected Deliverables
                </label>

                <textarea
                  name="deliverables"
                  value={
                    formData.deliverables
                  }
                  onChange={
                    handleChange
                  }
                  rows="6"
                  placeholder="e.g. Source code, responsive UI, API integration, documentation, deployment..."
                />

              </div>

              {/* ADDITIONAL NOTES */}

              <div className="fp-field full">

                <label>
                  Additional Notes
                </label>

                <textarea
                  name="additionalNotes"
                  value={
                    formData.additionalNotes
                  }
                  onChange={
                    handleChange
                  }
                  rows="4"
                  placeholder="Anything else freelancers should know?"
                />

              </div>

              {/* REVIEW */}

              <div className="fp-review full">

                <div className="fp-review-header">

                  <div>

                    <span>
                      PROJECT PREVIEW
                    </span>

                    <h3>
                      {formData.projectTitle ||
                        "Untitled Project"}
                    </h3>

                  </div>

                  <span className="fp-review-status">
                    READY TO POST
                  </span>

                </div>

                <div className="fp-review-grid">

                  <div>
                    <span>
                      Category
                    </span>

                    <strong>
                      {formData.category ||
                        "-"}
                    </strong>
                  </div>

                  <div>
                    <span>
                      Budget
                    </span>

                    <strong>
                      ₹
                      {Number(
                        formData.budgetMin ||
                          0
                      ).toLocaleString(
                        "en-IN"
                      )}

                      {" - "}

                      ₹
                      {Number(
                        formData.budgetMax ||
                          0
                      ).toLocaleString(
                        "en-IN"
                      )}
                    </strong>
                  </div>

                  <div>
                    <span>
                      Duration
                    </span>

                    <strong>
                      {formData.duration ||
                        "-"}
                    </strong>
                  </div>

                  <div>
                    <span>
                      Experience
                    </span>

                    <strong>
                      {formData.experienceRequired ||
                        "-"}
                    </strong>
                  </div>

                  <div>
                    <span>
                      Location
                    </span>

                    <strong>
                      {formData.locationType ===
                      "REMOTE"
                        ? "Remote"
                        : formData.location ||
                          "-"}
                    </strong>
                  </div>

                  <div>
                    <span>
                      Freelancers
                    </span>

                    <strong>
                      {formData.vacancies}
                    </strong>
                  </div>

                </div>

                <div className="fp-review-skills">

                  {formData.skills.map(
                    (skill) => (

                      <span key={skill}>
                        {skill}
                      </span>

                    )
                  )}

                </div>

              </div>

            </div>

          </div>

        )}

        {/* =================================================
            FOOTER
        ================================================= */}

        <div className="fp-footer">

          {step > 1 ? (

            <button
              type="button"
              className="fp-back-btn"
              onClick={
                previousStep
              }
              disabled={loading}
            >

              <ChevronLeft size={17} />

              Back

            </button>

          ) : (
            <div />
          )}

          {step < 4 ? (

            <button
              type="button"
              className="fp-primary-btn"
              onClick={
                nextStep
              }
              disabled={loading}
            >

              Continue

              <ChevronRight size={17} />

            </button>

          ) : (

            <button
              type="button"
              className="fp-primary-btn fp-post-btn"
              onClick={
                handlePostProject
              }
              disabled={loading}
            >

              {loading ? (
                <>
                  <span className="fp-spinner" />
                  Posting...
                </>
              ) : (
                <>
                  <CheckCircle2 size={17} />
                  Post Project
                </>
              )}

            </button>

          )}

        </div>

      </div>

      {/* =================================================
          CSS
      ================================================= */}

      <style>
        {freelanceProjectCSS}
      </style>

    </div>
  );
}

// =========================================================
// CSS
// =========================================================

const freelanceProjectCSS = `

* {
  box-sizing: border-box;
}

.fp-page {
  min-height: 100vh;
  padding: 28px;
  background:
    linear-gradient(
      135deg,
      #f8fafc 0%,
      #eef4ff 100%
    );
  color: #1e293b;
}

/* =====================================================
   HEADER
===================================================== */

.fp-header {
  max-width: 1180px;
  margin: 0 auto 24px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
}

.fp-header-left {
  display: flex;
  align-items: center;
  gap: 13px;
}

.fp-header-icon {
  width: 47px;
  height: 47px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 12px;
  background: #2563eb;
  color: white;
  box-shadow:
    0 7px 20px
    rgba(37,99,235,.2);
}

.fp-header h1 {
  margin: 0;
  font-size: 24px;
  font-weight: 800;
  color: #0f172a;
}

.fp-header p {
  margin: 4px 0 0;
  color: #64748b;
  font-size: 12px;
}

.fp-header-badge {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 9px 12px;
  border: 1px solid #dbeafe;
  border-radius: 8px;
  background: #eff6ff;
  color: #2563eb;
  font-size: 10px;
  font-weight: 750;
}

/* =====================================================
   API ERROR
===================================================== */

.fp-api-error {
  max-width: 1180px;
  margin: 0 auto 18px;
  padding: 13px 15px;
  display: flex;
  align-items: center;
  gap: 10px;
  border: 1px solid #fecaca;
  border-radius: 9px;
  background: #fef2f2;
  color: #991b1b;
  font-size: 10px;
}

.fp-api-error strong {
  font-size: 10px;
  white-space: nowrap;
}

.fp-api-error span {
  flex: 1;
  color: #b91c1c;
}

.fp-api-error button {
  width: 25px;
  height: 25px;
  border: none;
  background: transparent;
  color: #991b1b;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* =====================================================
   STEPPER
===================================================== */

.fp-stepper {
  max-width: 1180px;
  margin: 0 auto 22px;
  padding: 16px 20px;
  display: flex;
  align-items: center;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow:
    0 4px 14px
    rgba(15,23,42,.04);
}

.fp-step {
  display: flex;
  align-items: center;
  gap: 8px;
  color: #94a3b8;
  white-space: nowrap;
}

.fp-step-number {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #f1f5f9;
  color: #94a3b8;
  font-size: 10px;
  font-weight: 800;
}

.fp-step span {
  font-size: 10px;
  font-weight: 700;
}

.fp-step.active {
  color: #2563eb;
}

.fp-step.active
.fp-step-number {
  background: #2563eb;
  color: white;
}

.fp-step.completed {
  color: #15803d;
}

.fp-step.completed
.fp-step-number {
  background: #dcfce7;
  color: #15803d;
}

.fp-step-line {
  flex: 1;
  height: 1px;
  min-width: 25px;
  margin: 0 12px;
  background: #e2e8f0;
}

.fp-step-line.completed {
  background: #86efac;
}

/* =====================================================
   CONTAINER
===================================================== */

.fp-container {
  max-width: 1180px;
  margin: 0 auto;
}

/* =====================================================
   CARD
===================================================== */

.fp-card {
  padding: 25px;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 13px;
  box-shadow:
    0 5px 18px
    rgba(15,23,42,.045);
}

.fp-card-header {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding-bottom: 21px;
  margin-bottom: 22px;
  border-bottom: 1px solid #eef2f7;
}

.fp-section-icon {
  width: 39px;
  height: 39px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 9px;
  background: #eff6ff;
  color: #2563eb;
}

.fp-card-header h2 {
  margin: 1px 0 4px;
  font-size: 17px;
  font-weight: 800;
  color: #1e293b;
}

.fp-card-header p {
  margin: 0;
  color: #64748b;
  font-size: 11px;
}

/* =====================================================
   FORM GRID
===================================================== */

.fp-form-grid {
  display: grid;
  grid-template-columns:
    repeat(
      2,
      minmax(0, 1fr)
    );
  gap: 20px;
}

.fp-field {
  min-width: 0;
}

.fp-field.full {
  grid-column: 1 / -1;
}

.fp-field label {
  display: block;
  margin-bottom: 7px;
  color: #334155;
  font-size: 10px;
  font-weight: 750;
}

.fp-field label span {
  margin-left: 3px;
  color: #ef4444;
}

.fp-field input,
.fp-field select,
.fp-field textarea {
  width: 100%;
  border: 1px solid #dbe3ef;
  border-radius: 8px;
  background: #ffffff;
  color: #1e293b;
  outline: none;
  font-family: inherit;
  font-size: 11px;
  transition:
    border-color .2s,
    box-shadow .2s;
}

.fp-field input,
.fp-field select {
  height: 40px;
  padding: 0 11px;
}

.fp-field textarea {
  padding: 11px;
  resize: vertical;
  min-height: 100px;
  line-height: 1.6;
}

.fp-field input:focus,
.fp-field select:focus,
.fp-field textarea:focus {
  border-color: #60a5fa;
  box-shadow:
    0 0 0 3px
    rgba(37,99,235,.07);
}

.fp-field input::placeholder,
.fp-field textarea::placeholder {
  color: #a1aebe;
}

.fp-character-count {
  margin-top: 5px;
  text-align: right;
  color: #94a3b8;
  font-size: 9px;
}

.fp-error {
  display: block;
  margin-top: 5px;
  color: #dc2626;
  font-size: 9px;
}

/* =====================================================
   RADIO
===================================================== */

.fp-radio-group {
  display: grid;
  grid-template-columns:
    repeat(
      2,
      1fr
    );
  gap: 9px;
}

.fp-radio-card {
  min-height: 58px;
  display: flex !important;
  align-items: center;
  gap: 9px;
  margin: 0 !important;
  padding: 9px 10px;
  border: 1px solid #dbe3ef;
  border-radius: 8px;
  cursor: pointer;
}

.fp-radio-card:has(input:checked) {
  border-color: #60a5fa;
  background: #eff6ff;
}

.fp-radio-card input {
  width: 14px;
  height: 14px;
  accent-color: #2563eb;
}

.fp-radio-card div {
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.fp-radio-card strong {
  color: #334155;
  font-size: 10px;
}

.fp-radio-card span {
  margin: 0 !important;
  color: #94a3b8;
  font-size: 8px;
}

/* =====================================================
   SKILLS
===================================================== */

.fp-skill-input {
  display: flex;
  gap: 8px;
}

.fp-skill-input input {
  flex: 1;
}

.fp-skill-input button {
  height: 40px;
  display: flex;
  align-items: center;
  gap: 5px;
  padding: 0 13px;
  border: none;
  border-radius: 8px;
  background: #eff6ff;
  color: #2563eb;
  font-size: 10px;
  font-weight: 750;
  cursor: pointer;
}

.fp-skill-input button:hover {
  background: #dbeafe;
}

.fp-skills {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
  margin-top: 10px;
}

.fp-skill-tag {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 8px;
  border: 1px solid #bfdbfe;
  border-radius: 20px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 9px;
  font-weight: 700;
}

.fp-skill-tag button {
  width: 15px;
  height: 15px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  color: #2563eb;
  cursor: pointer;
}

/* =====================================================
   INPUT ICON
===================================================== */

.fp-input-icon {
  position: relative;
  display: flex;
  align-items: center;
}

.fp-input-icon svg {
  position: absolute;
  left: 11px;
  color: #94a3b8;
  pointer-events: none;
}

.fp-input-icon input {
  padding-left: 33px;
}

/* =====================================================
   REVIEW
===================================================== */

.fp-review {
  margin-top: 3px;
  padding: 17px;
  border: 1px solid #dbeafe;
  border-radius: 10px;
  background:
    linear-gradient(
      135deg,
      #f8fbff,
      #eff6ff
    );
}

.fp-review-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 15px;
  margin-bottom: 17px;
}

.fp-review-header span:first-child {
  color: #2563eb;
  font-size: 8px;
  font-weight: 800;
  letter-spacing: .7px;
}

.fp-review-header h3 {
  margin: 4px 0 0;
  color: #1e293b;
  font-size: 15px;
}

.fp-review-status {
  padding: 5px 8px;
  border-radius: 15px;
  background: #dcfce7;
  color: #15803d;
  font-size: 8px;
  font-weight: 800;
}

.fp-review-grid {
  display: grid;
  grid-template-columns:
    repeat(
      3,
      1fr
    );
  gap: 14px;
}

.fp-review-grid div {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.fp-review-grid span {
  color: #94a3b8;
  font-size: 8px;
}

.fp-review-grid strong {
  color: #334155;
  font-size: 10px;
}

.fp-review-skills {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 16px;
  padding-top: 14px;
  border-top: 1px solid #dbeafe;
}

.fp-review-skills span {
  padding: 5px 8px;
  border-radius: 15px;
  background: white;
  border: 1px solid #dbeafe;
  color: #2563eb;
  font-size: 8px;
  font-weight: 700;
}

/* =====================================================
   FOOTER
===================================================== */

.fp-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 15px;
  margin-top: 17px;
}

.fp-back-btn {
  height: 38px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0 14px;
  border: 1px solid #dbe3ef;
  border-radius: 8px;
  background: white;
  color: #475569;
  font-size: 10px;
  font-weight: 700;
  cursor: pointer;
}

.fp-back-btn:hover {
  background: #f8fafc;
}

.fp-primary-btn {
  height: 39px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  padding: 0 17px;
  border: none;
  border-radius: 8px;
  background: #2563eb;
  color: white;
  font-size: 10px;
  font-weight: 750;
  cursor: pointer;
  box-shadow:
    0 5px 13px
    rgba(37,99,235,.17);
  transition: .2s ease;
}

.fp-primary-btn:hover {
  background: #1d4ed8;
  transform:
    translateY(-1px);
}

.fp-primary-btn:disabled,
.fp-back-btn:disabled {
  opacity: .65;
  cursor: not-allowed;
  transform: none;
}

.fp-post-btn {
  padding-left: 19px;
  padding-right: 19px;
}

/* =====================================================
   SPINNER
===================================================== */

.fp-spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255,255,255,.4);
  border-top-color: white;
  border-radius: 50%;
  animation:
    fp-spin .7s linear infinite;
}

@keyframes fp-spin {
  to {
    transform: rotate(360deg);
  }
}

/* =====================================================
   SUCCESS
===================================================== */

.fp-success {
  max-width: 620px;
  margin: 70px auto;
  padding: 38px;
  text-align: center;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  box-shadow:
    0 10px 30px
    rgba(15,23,42,.07);
}

.fp-success-icon {
  width: 76px;
  height: 76px;
  margin: 0 auto 17px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #dcfce7;
  color: #16a34a;
}

.fp-success h1 {
  margin: 0 0 8px;
  color: #0f172a;
  font-size: 22px;
}

.fp-success > p {
  margin: 0 auto 22px;
  max-width: 470px;
  color: #64748b;
  font-size: 11px;
  line-height: 1.7;
}

.fp-success-details {
  display: grid;
  grid-template-columns:
    repeat(
      3,
      1fr
    );
  gap: 10px;
  margin-bottom: 15px;
  text-align: left;
}

.fp-success-details div {
  padding: 11px;
  border-radius: 8px;
  background: #f8fafc;
}

.fp-success-details span {
  display: block;
  margin-bottom: 4px;
  color: #94a3b8;
  font-size: 8px;
}

.fp-success-details strong {
  display: block;
  color: #334155;
  font-size: 10px;
}

.fp-created-id {
  margin-bottom: 22px;
  color: #64748b;
  font-size: 10px;
}

.fp-created-id strong {
  margin-left: 5px;
  color: #16a34a;
}

/* =====================================================
   RESPONSIVE
===================================================== */

@media (max-width: 900px) {

  .fp-step span {
    display: none;
  }

  .fp-stepper {
    padding: 13px;
  }

  .fp-step-line {
    margin: 0 8px;
  }

  .fp-review-grid {
    grid-template-columns:
      repeat(
        2,
        1fr
      );
  }

}

@media (max-width: 700px) {

  .fp-page {
    padding: 15px;
  }

  .fp-header {
    align-items:
      flex-start;
    flex-direction:
      column;
  }

  .fp-header-badge {
    width: 100%;
    justify-content: center;
  }

  .fp-api-error {
    align-items:
      flex-start;
    flex-direction:
      column;
  }

  .fp-card {
    padding: 17px;
  }

  .fp-form-grid {
    grid-template-columns:
      1fr;
  }

  .fp-field.full {
    grid-column: auto;
  }

  .fp-radio-group {
    grid-template-columns:
      1fr;
  }

  .fp-stepper {
    overflow-x: auto;
  }

  .fp-stepper::-webkit-scrollbar {
    display: none;
  }

  .fp-step {
    flex-shrink: 0;
  }

  .fp-step-line {
    flex: 1;
  }

  .fp-review-grid {
    grid-template-columns:
      1fr 1fr;
  }

  .fp-success-details {
    grid-template-columns:
      1fr;
  }

}

@media (max-width: 450px) {

  .fp-review-grid {
    grid-template-columns:
      1fr;
  }

  .fp-footer {
    flex-direction:
      column-reverse;
  }

  .fp-footer > button,
  .fp-footer > div {
    width: 100%;
  }

  .fp-back-btn,
  .fp-primary-btn {
    width: 100%;
  }

}

`;

export default FreelanceProjectPost;